// src/data/helpCenterData.ts
// Datos estructurados para el Centro de Ayuda de AMPI Riviera Nayarit

export interface HelpSection {
  id: string;
  title: string;
  icon: string;
  description: string;
  articles: HelpArticle[];
}

export interface HelpArticle {
  id: string;
  title: string;
  content: string;
  tags: string[];
  role?: 'all' | 'admin' | 'comite' | 'asesor' | 'asociado';
}

export interface FAQItem {
  id: string;
  question: string;
  answer: string;
  category: string;
}

// =====================================================
// SECCIONES DEL CENTRO DE AYUDA
// =====================================================

export const helpSections: HelpSection[] = [
  {
    id: 'getting-started',
    title: 'Primeros Pasos',
    icon: 'fa-solid fa-rocket',
    description: 'Aprende lo básico para comenzar a usar la plataforma',
    articles: [
      {
        id: 'intro-platform',
        title: '¿Qué es la Plataforma AMPI Riviera Nayarit?',
        tags: ['introducción', 'plataforma', 'ampi'],
        content: `
## Bienvenido a AMPI Riviera Nayarit

La plataforma AMPI Riviera Nayarit es un sistema integral diseñado para la Asociación Mexicana de Profesionales Inmobiliarios, Sección Riviera Nayarit.

### Funcionalidades Principales

- **Gestión de Miembros:** Registro y administración de asesores inmobiliarios (afiliados) y asociados.
- **Catálogo de Propiedades:** Publicación y búsqueda de propiedades inmobiliarias.
- **Capacitación Continua:** Organización de talleres, eventos y cursos con sistema de créditos AMPI.
- **Directorio Profesional:** Visibilidad de agentes, asociados e inmobiliarias.
- **Sistema de Leads:** Contacto directo entre clientes y profesionales inmobiliarios.

### Tipos de Usuario

| Rol | Descripción |
|-----|-------------|
| **Administrador General** | Control total del sistema |
| **Administrador de Eventos** | Gestión de capacitación y talleres |
| **Afiliado (Asesor)** | Miembro profesional AMPI |
| **Asociado** | Miembro asociado AMPI |
        `
      },
      {
        id: 'login-guide',
        title: 'Cómo Iniciar Sesión',
        tags: ['login', 'acceso', 'contraseña'],
        content: `
## Iniciar Sesión en la Plataforma

### Pasos para Acceder

1. **Ubica el botón de login:** En la esquina superior derecha de la página, haz clic en el ícono de usuario o el texto "Iniciar Sesión".

2. **Ingresa tus credenciales:**
   - Correo electrónico registrado
   - Contraseña

3. **Opciones adicionales:**
   - Marca "Recordar correo" para guardar tu email en este dispositivo
   - Usa "¿Olvidaste tu contraseña?" si necesitas recuperar acceso

4. **Haz clic en "Iniciar Sesión"**

### Después del Login

- Si eres **Administrador de Comité**, serás redirigido a seleccionar tu rol de trabajo
- Si eres **Asesor o Asociado**, irás directamente a tu Dashboard personal
- Si eres **Administrador General**, accederás al panel de administración

### Problemas Comunes

**No puedo iniciar sesión:**
- Verifica que tu correo esté escrito correctamente
- Asegúrate de usar la contraseña correcta (distingue mayúsculas/minúsculas)
- Contacta al administrador si tu cuenta está inactiva
        `
      },
      {
        id: 'password-recovery',
        title: 'Recuperar Contraseña',
        tags: ['contraseña', 'recuperar', 'olvidé'],
        content: `
## Recuperar tu Contraseña

Si olvidaste tu contraseña, sigue estos pasos:

### Proceso de Recuperación

1. **Abre el modal de login** haciendo clic en "Iniciar Sesión"

2. **Haz clic en "¿Olvidaste tu contraseña?"**

3. **Ingresa tu correo electrónico** registrado en la plataforma

4. **Haz clic en "Enviar Email de Recuperación"**

5. **Revisa tu bandeja de entrada** (y la carpeta de spam)

6. **Sigue el enlace** en el correo para crear una nueva contraseña

### Notas Importantes

- El sistema verifica que tu correo exista en la base de datos antes de enviar el email
- El enlace de recuperación tiene una duración limitada
- Si no recibes el correo en 5 minutos, intenta de nuevo
- El correo proviene de Firebase (noreply@firebase.com)
        `
      },
      {
        id: 'roles-explained',
        title: 'Tipos de Usuarios y Permisos',
        tags: ['roles', 'permisos', 'usuarios'],
        content: `
## Roles en la Plataforma

### Administrador General

**Acceso completo** a todas las funcionalidades:
- Gestión de usuarios (crear, editar, desactivar)
- Administración de propiedades
- Control de talleres y eventos
- Gestión de inmobiliarias
- Reportes y estadísticas

### Administrador de Eventos (Comité)

**Enfocado en capacitación:**
- Crear y editar talleres/eventos
- Gestionar inscripciones
- Escanear asistencia con QR
- Ver reportes de participación

*No tiene acceso a:* gestión de usuarios, propiedades o inmobiliarias.

### Afiliado (Asesor)

**Miembro profesional activo:**
- Perfil profesional público
- Publicación de propiedades
- Inscripción a talleres
- Sistema de créditos AMPI
- Gestión de certificaciones

### Asociado

**Miembro asociado:**
- Perfil profesional
- Inscripción a talleres
- Sistema de créditos AMPI
- Gestión de certificaciones

*No tiene acceso a:* publicación de propiedades.
        `
      }
    ]
  },
  {
    id: 'admin-guide',
    title: 'Guía del Administrador',
    icon: 'fa-solid fa-user-shield',
    description: 'Manual completo para administradores del sistema',
    articles: [
      {
        id: 'admin-dashboard',
        title: 'Dashboard de Administración',
        tags: ['dashboard', 'admin', 'panel'],
        role: 'admin',
        content: `
## Panel de Administración

### Acceso
**Ruta:** \`/admin-dashboard\`

Después de iniciar sesión como administrador, serás redirigido automáticamente al Dashboard.

### Tarjetas de Resumen

El dashboard muestra información clave en tarjetas:

- **Total de Propiedades:** Inmuebles registrados en el sistema
- **Total de Asesores:** Miembros afiliados activos
- **Total de Asociados:** Miembros asociados activos
- **Talleres Programados:** Próximos eventos de capacitación

### Navegación Rápida

Desde el dashboard puedes acceder a:
- Gestión de Talleres
- Lista de Propiedades
- Administración de Usuarios
- Reportes y Estadísticas
        `
      },
      {
        id: 'manage-workshops',
        title: 'Gestión de Talleres y Eventos',
        tags: ['talleres', 'eventos', 'crear', 'editar'],
        role: 'admin',
        content: `
## Administración de Talleres

### Ver Lista de Talleres
**Ruta:** \`/admin-talleres\`

La tabla muestra:
| Columna | Descripción |
|---------|-------------|
| Nombre | Título del taller |
| Fecha | Fecha programada |
| Horario | Inicio y término |
| Lugar | Sede del evento |
| Organizador | Responsable |
| Cupo | Capacidad máxima |
| Inscritos | Participantes actuales |
| Estado | Próximo, En Curso, Finalizado |

### Filtros Disponibles
- Nombre del taller
- Mes específico
- Tipo de costo (gratuito/pago)
- Con/sin créditos AMPI
- Estado del evento

### Crear Nuevo Taller
**Ruta:** \`/admin-create-taller\`

1. Clic en **"+ Agregar Taller"**
2. Completa la Ficha Técnica:
   - Información Básica (nombre, fecha, horario)
   - Ubicación (lugar, dirección, ciudad)
   - Organización (responsable, contacto)
   - Detalles (tipo, cupo, objetivo)
   - Actividades incluidas
   - Ponentes confirmados
   - Agenda tentativa
   - Registro y costo
   - Patrocinadores
   - Enlaces de comunicación

### Acciones sobre Talleres

**Editar:** Modifica cualquier campo del taller
**Duplicar:** Crea una copia para eventos similares (sin inscritos)
**Eliminar:** Borra el taller permanentemente
**Ver Detalles:** Información completa e inscritos
        `
      },
      {
        id: 'scan-attendance',
        title: 'Escanear Asistencia con QR',
        tags: ['qr', 'asistencia', 'escanear'],
        role: 'admin',
        content: `
## Sistema de Asistencia por QR

### Acceso
**Ruta:** \`/escanear-asistencia\`

### Cómo Funciona

1. **Selecciona el taller activo** del menú desplegable

2. **Activa la cámara** del dispositivo (el navegador pedirá permiso)

3. **Escanea el código QR** del participante

4. **El sistema registra automáticamente:**
   - Nombre del participante
   - Hora de registro
   - Confirmación visual

### Requisitos

- Dispositivo con cámara (laptop, tablet, smartphone)
- Navegador moderno (Chrome, Firefox, Safari)
- Conexión a internet estable

### Solución de Problemas

**La cámara no funciona:**
- Verifica permisos del navegador
- Prueba en otro navegador
- Asegúrate de que no haya otra app usando la cámara

**El QR no se lee:**
- Mejora la iluminación
- Mantén el QR estable
- Acerca o aleja el dispositivo
        `
      },
      {
        id: 'manage-users',
        title: 'Gestión de Usuarios',
        tags: ['usuarios', 'crear', 'cuentas'],
        role: 'admin',
        content: `
## Administración de Usuarios

### Crear Nueva Cuenta
**Ruta:** \`/create-account\`

**Tipos de cuenta disponibles:**
- Asesor (Afiliado)
- Asociado
- Administrador

**Campos requeridos:**
- Nombre completo
- Correo electrónico (será el login)
- Teléfono de contacto
- Inmobiliaria asignada (para asesores)
- Contraseña temporal

### Importación Masiva
**Ruta:** \`/migrar-nuevos-miembros\`

Para importar múltiples usuarios desde archivo externo.

### Verificar Inmobiliarias
**Ruta:** \`/verificar-inmobiliarias-nuevas\`

Panel para revisar y aprobar nuevas inmobiliarias.
        `
      }
    ]
  },
  {
    id: 'committee-guide',
    title: 'Guía del Comité de Eventos',
    icon: 'fa-solid fa-calendar-check',
    description: 'Manual para administradores de eventos y capacitación',
    articles: [
      {
        id: 'committee-dashboard',
        title: 'Dashboard del Comité',
        tags: ['comité', 'dashboard', 'eventos'],
        role: 'comite',
        content: `
## Panel del Administrador de Comité

### Acceso
**Ruta:** \`/admin-comite-dashboard\`

### Selección de Rol

Al iniciar sesión, si tienes permisos de comité, verás la pantalla de selección:
- **Panel de Administrador de Comité:** Para gestionar eventos
- **Mi Dashboard Personal:** Para usar la plataforma como miembro

### Funciones Disponibles

Como Admin de Comité puedes:
- ✅ Crear y editar talleres/eventos
- ✅ Gestionar inscripciones
- ✅ Escanear asistencia
- ✅ Ver reportes de participación

No tienes acceso a:
- ❌ Gestión de usuarios
- ❌ Administración de propiedades
- ❌ Configuración del sistema
        `
      },
      {
        id: 'committee-workflow',
        title: 'Flujo de Trabajo para Eventos',
        tags: ['flujo', 'proceso', 'eventos'],
        role: 'comite',
        content: `
## Ciclo de Vida de un Evento

### 1. Planificación
- Define objetivo, fecha, lugar y ponentes
- Prepara material promocional (banner)

### 2. Creación en Sistema
- Usa la Ficha Técnica completa
- Configura cupo, costo y créditos AMPI
- Agrega agenda y ponentes

### 3. Publicación
- Activa el evento para inscripciones
- Comparte enlaces de registro

### 4. Gestión Pre-evento
- Monitorea inscripciones
- Revisa estados de pago
- Comunica recordatorios

### 5. Día del Evento
- Usa el escáner QR para asistencia
- Verifica participantes en tiempo real

### 6. Post-evento
- Revisa estadísticas de asistencia
- Los créditos se asignan automáticamente
- Genera reportes si es necesario
        `
      },
      {
        id: 'manage-inscriptions',
        title: 'Gestionar Inscripciones',
        tags: ['inscripciones', 'inscritos', 'participantes'],
        role: 'comite',
        content: `
## Gestión de Inscripciones

### Ver Inscritos de un Taller
**Ruta:** \`/ver-taller-ind/:id\`

Información por participante:
- Nombre completo
- Correo electrónico
- Estado de pago (Completado/Pendiente)
- Asistencia (Registrada/Sin registrar)
- Código QR único

### Inscribir Manualmente
**Ruta:** \`/ins-taller/:id\`

1. Busca el usuario por correo o nombre
2. Selecciona de la lista
3. Confirma la inscripción
4. El sistema genera el código QR automáticamente

### Estados de Inscripción

| Estado Pago | Descripción |
|-------------|-------------|
| Completado | Pago verificado |
| Pendiente | Esperando pago |

| Estado Asistencia | Descripción |
|-------------------|-------------|
| Registrada | QR escaneado el día del evento |
| Sin registrar | No se ha validado asistencia |
        `
      }
    ]
  },
  {
    id: 'member-guide',
    title: 'Guía para Miembros',
    icon: 'fa-solid fa-user',
    description: 'Manual para asesores y asociados',
    articles: [
      {
        id: 'member-dashboard',
        title: 'Tu Dashboard Personal',
        tags: ['dashboard', 'miembro', 'inicio'],
        role: 'asesor',
        content: `
## Dashboard de Miembro

### Acceso
**Ruta:** \`/dashboard\`

### Información Disponible

Tu dashboard muestra:
- **Resumen de perfil:** Foto, nombre, inmobiliaria
- **Propiedades:** Tus inmuebles publicados (solo asesores)
- **Talleres:** Próximos eventos inscritos
- **Créditos AMPI:** Puntos acumulados

### Navegación Rápida

Desde el menú lateral accede a:
- Mi Perfil
- Mis Propiedades (asesores)
- Mis Cursos/Talleres
- Favoritos
        `
      },
      {
        id: 'edit-profile',
        title: 'Editar tu Perfil Profesional',
        tags: ['perfil', 'editar', 'foto'],
        content: `
## Tu Perfil Profesional

### Acceso
**Ruta:** \`/my-profile\`

### Secciones del Perfil

#### Foto de Perfil
- Clic en "Seleccionar Foto"
- Formatos: JPG, PNG, GIF
- Tamaño máximo: 5MB
- Se actualiza en toda la plataforma

#### Datos Personales
- Nombre completo
- Correo electrónico
- Teléfono de contacto
- Años de experiencia
- Biografía profesional

#### Redes Sociales
- Facebook
- LinkedIn
- Instagram
- TikTok
- YouTube

#### Certificaciones
- Nombre de la certificación
- Institución que la otorga
- Año de obtención
- Imagen del certificado (opcional)

### Guardar Cambios

Después de editar, haz clic en **"Guardar Cambios"** para actualizar tu información.
        `
      },
      {
        id: 'my-workshops',
        title: 'Mis Talleres Inscritos',
        tags: ['talleres', 'cursos', 'qr'],
        content: `
## Gestión de Talleres Inscritos

### Acceso
**Ruta:** \`/mis-cursos\`

### Vista de Talleres

Tus talleres se organizan en:
- **Próximos Talleres:** Eventos por venir
- **Talleres Finalizados:** Historial de participación

### Información por Taller

- Nombre del evento
- Fecha y horario
- Lugar/Ubicación
- Organizador
- Estado de pago
- Asistencia registrada
- Créditos AMPI

### Tu Código QR

Cada inscripción tiene un QR único:

1. **Ver QR:** Abre el código en pantalla
2. **Descargar QR:** Guarda imagen PNG

**Importante:** Presenta este código el día del evento para registrar tu asistencia.

### Filtros Disponibles

- Nombre del taller
- Estado (Próximo, En Curso, Finalizado)
- Estatus de pago
- Asistencia
        `
      },
      {
        id: 'publish-property',
        title: 'Publicar una Propiedad',
        tags: ['propiedad', 'publicar', 'inmueble'],
        role: 'asesor',
        content: `
## Publicación de Propiedades

*Esta función está disponible solo para Asesores (Afiliados).*

### Acceso
**Ruta:** \`/submit-property\`

### Información Requerida

#### Datos Básicos
- Tipo de operación (Venta/Renta)
- Tipo de propiedad (Casa, Depto, Terreno, etc.)
- Precio y moneda

#### Ubicación
- Dirección completa
- Colonia/Fraccionamiento
- Ciudad y estado
- Código postal

#### Características
- Superficie (m² construcción y terreno)
- Recámaras
- Baños completos y medios
- Estacionamientos
- Antigüedad

#### Amenidades
- Selección múltiple de características
- Alberca, jardín, seguridad, etc.

#### Descripción
- Texto detallado del inmueble
- Destacar puntos fuertes

#### Galería
- Mínimo recomendado: 5 fotos
- Incluir fachada, interiores, áreas comunes

### Ver Mis Propiedades
**Ruta:** \`/my-property\`

Lista de tus propiedades con:
- Estado de publicación
- Estadísticas de vistas
- Leads recibidos
        `
      }
    ]
  },
  {
    id: 'faq',
    title: 'Preguntas Frecuentes',
    icon: 'fa-solid fa-circle-question',
    description: 'Respuestas a las dudas más comunes',
    articles: []
  }
];

// =====================================================
// PREGUNTAS FRECUENTES
// =====================================================

export const faqItems: FAQItem[] = [
  // Acceso y Cuenta
  {
    id: 'faq-1',
    category: 'Acceso y Cuenta',
    question: '¿Cómo obtengo una cuenta en la plataforma?',
    answer: 'Las cuentas son creadas por el administrador de AMPI Riviera Nayarit. Contacta a la sección local para solicitar acceso como miembro afiliado o asociado.'
  },
  {
    id: 'faq-2',
    category: 'Acceso y Cuenta',
    question: 'No recuerdo mi contraseña, ¿qué hago?',
    answer: 'Usa la opción "¿Olvidaste tu contraseña?" en el modal de inicio de sesión. Ingresa tu correo registrado y recibirás un enlace para restablecerla. Revisa también tu carpeta de spam.'
  },
  {
    id: 'faq-3',
    category: 'Acceso y Cuenta',
    question: '¿Por qué no puedo iniciar sesión?',
    answer: 'Verifica que: 1) Tu correo esté escrito correctamente, 2) Tu contraseña sea la correcta (distingue mayúsculas/minúsculas), 3) Tu cuenta esté activa. Si el problema persiste, contacta al administrador.'
  },
  {
    id: 'faq-4',
    category: 'Acceso y Cuenta',
    question: '¿Puedo cambiar mi correo electrónico?',
    answer: 'El correo es tu identificador único de cuenta y no puede cambiarse directamente. Para modificarlo, contacta al administrador del sistema.'
  },

  // Talleres y Eventos
  {
    id: 'faq-5',
    category: 'Talleres y Eventos',
    question: '¿Cómo me inscribo a un taller?',
    answer: 'Navega a la sección de talleres disponibles, selecciona el evento de tu interés, revisa los detalles y haz clic en "Inscribirme". Si tiene costo, deberás completar el pago.'
  },
  {
    id: 'faq-6',
    category: 'Talleres y Eventos',
    question: '¿Dónde encuentro mi código QR de asistencia?',
    answer: 'Ve a "Mis Cursos" en tu dashboard. Para cada taller inscrito verás los botones "Ver QR" (para visualizar) y "Descargar QR" (para guardar la imagen).'
  },
  {
    id: 'faq-7',
    category: 'Talleres y Eventos',
    question: '¿Cómo se registra mi asistencia?',
    answer: 'El día del evento, un organizador escaneará tu código QR con la aplicación. El sistema registrará automáticamente tu asistencia y recibirás una confirmación.'
  },
  {
    id: 'faq-8',
    category: 'Talleres y Eventos',
    question: '¿Los créditos AMPI se asignan automáticamente?',
    answer: 'Sí, los créditos se asignan cuando completas la asistencia (tu QR es escaneado) en un taller que otorga créditos. Puedes ver tu acumulado en tu perfil.'
  },
  {
    id: 'faq-9',
    category: 'Talleres y Eventos',
    question: '¿Puedo cancelar mi inscripción a un taller?',
    answer: 'Para cancelar una inscripción, contacta al organizador del evento o al administrador. Las políticas de reembolso dependen de cada evento.'
  },

  // Propiedades
  {
    id: 'faq-10',
    category: 'Propiedades',
    question: '¿Cuántas propiedades puedo publicar?',
    answer: 'El número de propiedades depende de tu plan de membresía. Consulta con el administrador de tu sección para conocer los límites de tu cuenta.'
  },
  {
    id: 'faq-11',
    category: 'Propiedades',
    question: '¿Por qué mi propiedad no aparece en la búsqueda?',
    answer: 'Verifica que: 1) La propiedad esté marcada como "activa", 2) Tenga toda la información requerida completa, 3) Las fotos estén correctamente cargadas.'
  },
  {
    id: 'faq-12',
    category: 'Propiedades',
    question: '¿Cómo recibo leads de mis propiedades?',
    answer: 'Cuando un cliente se interesa en tu propiedad y envía un mensaje, recibirás una notificación al correo registrado en tu perfil. También aparecerá en tu dashboard.'
  },
  {
    id: 'faq-13',
    category: 'Propiedades',
    question: '¿Por qué no puedo publicar propiedades?',
    answer: 'La publicación de propiedades está disponible solo para miembros Afiliados (Asesores). Si eres Asociado, este módulo no está habilitado para tu cuenta.'
  },

  // Perfil
  {
    id: 'faq-14',
    category: 'Perfil',
    question: '¿Cómo actualizo mi foto de perfil?',
    answer: 'Ve a "Mi Perfil", en la sección "Foto de Perfil" haz clic en "Seleccionar Foto". Elige una imagen (JPG, PNG o GIF, máximo 5MB) y se actualizará automáticamente.'
  },
  {
    id: 'faq-15',
    category: 'Perfil',
    question: '¿Cómo agrego mis certificaciones?',
    answer: 'En "Mi Perfil", baja a la sección "Mis Certificaciones", haz clic en "Agregar Certificación" e ingresa el nombre, institución y año. Opcionalmente puedes subir una imagen del certificado.'
  },
  {
    id: 'faq-16',
    category: 'Perfil',
    question: '¿Mi perfil es visible públicamente?',
    answer: 'Sí, tu perfil profesional es visible en el directorio de agentes/asociados. La información que completes (bio, redes sociales, certificaciones) ayuda a generar confianza con clientes potenciales.'
  }
];

// =====================================================
// GLOSARIO
// =====================================================

export interface GlossaryTerm {
  term: string;
  definition: string;
}

export const glossaryTerms: GlossaryTerm[] = [
  { term: 'Afiliado', definition: 'Asesor inmobiliario con membresía activa en AMPI, puede publicar propiedades y acceder a todos los beneficios de la asociación.' },
  { term: 'Asociado', definition: 'Profesional relacionado al sector inmobiliario (notario, arquitecto, etc.) que es miembro de AMPI pero no ejerce como asesor directo.' },
  { term: 'Créditos AMPI', definition: 'Puntos de capacitación continua que se obtienen al asistir a talleres y eventos. Son requeridos para mantener certificaciones profesionales vigentes.' },
  { term: 'Dashboard', definition: 'Panel de control principal donde se muestra un resumen de tu actividad, accesos rápidos y estadísticas relevantes.' },
  { term: 'Ficha Técnica', definition: 'Formulario completo que se utiliza para registrar todos los detalles de un evento o taller en el sistema.' },
  { term: 'Inmobiliaria', definition: 'Empresa o despacho de servicios inmobiliarios donde laboran los asesores afiliados a AMPI.' },
  { term: 'Lead', definition: 'Contacto de un cliente potencial que muestra interés en una propiedad y solicita información o visita.' },
  { term: 'QR de Asistencia', definition: 'Código único generado para cada inscripción a un taller, que se escanea el día del evento para confirmar la asistencia del participante.' },
  { term: 'Taller', definition: 'Evento de capacitación organizado por AMPI que puede ser presencial, virtual o híbrido, y puede otorgar créditos de educación continua.' }
];

// =====================================================
// FUNCIÓN DE BÚSQUEDA
// =====================================================

export const searchHelpContent = (query: string): (HelpArticle | FAQItem)[] => {
  const lowerQuery = query.toLowerCase().trim();
  if (!lowerQuery) return [];

  const results: (HelpArticle | FAQItem)[] = [];

  // Buscar en artículos
  helpSections.forEach(section => {
    section.articles.forEach(article => {
      const matchTitle = article.title.toLowerCase().includes(lowerQuery);
      const matchContent = article.content.toLowerCase().includes(lowerQuery);
      const matchTags = article.tags.some(tag => tag.toLowerCase().includes(lowerQuery));

      if (matchTitle || matchContent || matchTags) {
        results.push(article);
      }
    });
  });

  // Buscar en FAQ
  faqItems.forEach(faq => {
    const matchQuestion = faq.question.toLowerCase().includes(lowerQuery);
    const matchAnswer = faq.answer.toLowerCase().includes(lowerQuery);

    if (matchQuestion || matchAnswer) {
      results.push(faq);
    }
  });

  return results;
};
